import React, { useState, useEffect } from 'react';
import { navigationItems, portfolioData } from '../mock';
import { X, Plus, RotateCcw, ChevronLeft, ChevronRight, MoreVertical, Minus, Square, X as CloseIcon, Mail, Linkedin, Github, BookOpen } from 'lucide-react';

const BrowserUI = ({ children }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [isMaximized, setIsMaximized] = useState(true);
  const [isManualScroll, setIsManualScroll] = useState(false);

  useEffect(() => {
    // SEO: update title (fixed) and meta description on active tab change
    const descs = {
      home: 'Portfolio of Jon Ortega Uzquiaga, Data & AI Engineer based in Bilbao (Basque Country). AI solutions and Full‑Stack development.',
      wrappers: 'Startup by Jon Ortega. AI agents and practical AI solutions for business. Full‑Stack in Bilbao.',
      experience: 'Work experience of Jon Ortega as an AI/Full‑Stack engineer. Roles, impact and timeline.',
      projects: 'Selected projects by Jon Ortega: AI, Full‑Stack and production‑ready solutions.',
      tech: 'Technologies and tools used by Jon Ortega across AI, backend, frontend and cloud.',
      posts: 'Articles and posts by Jon Ortega about AI, engineering and product.',
      contact: 'Contact Jon Ortega in Bilbao (Basque Country). Open to collaborations.'
    };
    const title = 'Portfolio | Jon Ortega';
    const desc = descs[activeTab] || 'Portfolio of Jon Ortega · Data & AI Engineer';
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', desc);
  }, [activeTab]);
  
  useEffect(() => {
    const handleScroll = () => {
      // Skip automatic scroll detection during manual scroll
      if (isManualScroll) return;

      const sections = navigationItems.map(item => ({
        id: item.id,
        element: document.getElementById(item.id),
      })).filter(section => section.element);

      // Find the section that's currently most visible in the content area
      const contentArea = document.querySelector('.overflow-auto');
      if (contentArea) {
        const contentRect = contentArea.getBoundingClientRect();
        const currentSection = sections.find(section => {
          const rect = section.element.getBoundingClientRect();
          // Check if section is visible within the content area
          return rect.top <= (contentRect.top + 150) && rect.bottom >= (contentRect.top + 150);
        });

        if (currentSection && currentSection.id !== activeTab) {
          setActiveTab(currentSection.id);
        }
      }
    };

    // Listen to both window scroll and content area scroll
    const contentArea = document.querySelector('.overflow-auto');
    if (contentArea) {
      contentArea.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      if (contentArea) {
        contentArea.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isManualScroll, activeTab]);

  const scrollToSection = (sectionId) => {
    console.log(`Trying to scroll to section: ${sectionId}`);
    
    // Each tab should go to its corresponding section
    const targetSectionId = sectionId;
    console.log(`Mapping ${sectionId} -> ${targetSectionId}`);
    
    const element = document.getElementById(targetSectionId);
    console.log(`Element found:`, element);
    
    if (element) {
      // Set manual scroll flag to prevent automatic scroll detection
      setIsManualScroll(true);
      
      // Update tab immediately to the CLICKED tab (not the target)
      setActiveTab(sectionId);
      
      // Scroll inside the content area only (keep nav fixed)
      console.log(`Scrolling to ${targetSectionId} within content area to show ${sectionId}`);
      try {
        const contentArea = document.querySelector('.overflow-auto');
        if (contentArea) {
          const contentRect = contentArea.getBoundingClientRect();
          const elemRect = element.getBoundingClientRect();
          const targetTop = contentArea.scrollTop + (elemRect.top - contentRect.top);
          contentArea.scrollTo({ top: targetTop, behavior: 'smooth' });
          console.log('Content area scroll executed successfully');
        } else {
          element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
      } catch (e) {
        console.error('Scroll failed:', e);
      }
      
      // Update the URL to the clicked section
      window.history.pushState({}, '', `#${sectionId}`);
      
      // Re-enable automatic scroll detection after scroll completes
      setTimeout(() => {
        setIsManualScroll(false);
        console.log('Manual scroll flag reset');
      }, 800);
    } else {
      console.error(`Section with id "${targetSectionId}" not found!`);
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

      const getCurrentUrl = () => {
      const currentSection = navigationItems.find(item => item.id === activeTab);
      const path = currentSection?.href?.replace('#', '/') || '/';
      return `jon.ortega${path}`;
    };

    const getCurrentPath = () => {
      const currentSection = navigationItems.find(item => item.id === activeTab);
      return currentSection?.href?.replace('#', '/') || '/';
    };

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Window Controls */}
      <div className="h-8 bg-gray-200 flex items-center justify-between px-2 select-none">
        <div className="flex items-center space-x-2">
          <div className="flex space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-600 cursor-pointer"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500 hover:bg-yellow-600 cursor-pointer" 
                 onClick={() => setIsMaximized(false)}></div>
            <div className="w-3 h-3 rounded-full bg-green-500 hover:bg-green-600 cursor-pointer"
                 onClick={() => setIsMaximized(true)}></div>
          </div>
        </div>
        <div className="text-xs text-gray-600 font-medium">Portfolio | Jon Ortega</div>
        <div className="w-16"></div>
      </div>

      {/* Tab Bar */}
      <div className="h-10 bg-gray-100 border-b border-gray-300 flex items-end">
        <div className="flex h-full pl-2 sm:pl-0">
          {navigationItems.map((item, index) => (
            <div
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`h-full px-3 sm:px-4 py-2 text-sm cursor-pointer flex items-center space-x-2 border-t border-l border-r border-gray-300 relative transition-all duration-200 ${
                activeTab === item.id
                  ? 'bg-white border-b-0 -mb-px z-10 transform scale-105'
                  : 'bg-gray-200 hover:bg-gray-100'
              } ${index === 0 ? 'ml-2' : '-ml-px'} ${['tech','contact'].includes(item.id) ? 'hidden sm:flex' : ''}`}
              style={{
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
              }}
            >
              <span className="text-xs max-w-24 truncate font-medium">{item.label}</span>
            </div>
          ))}
          <div className="h-full px-3 py-2 hover:bg-gray-200 cursor-pointer hidden sm:flex items-center transition-colors">
            <Plus className="w-4 h-4 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Address Bar */}
      <div className="h-10 bg-white border-b border-gray-300 flex items-center px-4 space-x-3">
        <div className="flex items-center space-x-2">
          <button className="p-1.5 hover:bg-gray-100 rounded-full disabled:opacity-50 transition-colors hidden sm:inline-flex">
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          <button className="p-1.5 hover:bg-gray-100 rounded-full disabled:opacity-50 transition-colors hidden sm:inline-flex">
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
          <button 
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            onClick={handleReload}
          >
            <RotateCcw className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 bg-gray-100 rounded-full px-4 py-1 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            🔒 <span className="hidden sm:inline">jon.ortega</span>{getCurrentPath()}
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {portfolioData.socialLinks.map((link, index) => {
              const isX = link.name.toLowerCase().includes('x');
              const iconMap = { Mail, Linkedin, Github, BookOpen };
              const IconComponent = iconMap[link.icon];
              return (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-7 h-7 rounded-full transition-colors flex items-center justify-center border border-gray-300 bg-white/70 hover:bg-white"
                  title={link.name}
                >
                  {isX ? (
                    <img src={process.env.PUBLIC_URL + '/icons/x.svg'} alt="X" className="w-4 h-4" />
                  ) : (
                    IconComponent ? <IconComponent className="w-4 h-4 text-gray-700" /> : null
                  )}
                </a>
              );
            })}
          </div>
        </div>

        <button className="p-1.5 hover:bg-gray-100 rounded-full transition-colors hidden sm:inline-flex">
          <MoreVertical className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-white overflow-auto">
        {children}
      </div>
    </div>
  );
};

export default BrowserUI;